import React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import ValueBookiesFilter from "./ValueBookiesFilter";
import { getSportsIcon, sportsIcons } from "../../constants";

const ValuePageFilters = ({
  extraFilters,
  bookmakers,
  onExtraFiltersChange,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Helper function to ensure sport value is always an array
  const ensureArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.5,
      fontSize: "0.82rem",
      background: isDark
        ? alpha("#ffffff", 0.03)
        : alpha("#ffffff", 0.7),
      "& fieldset": {
        borderColor: isDark
          ? alpha("#ffffff", 0.08)
          : alpha("#000000", 0.1),
      },
      "&:hover fieldset": {
        borderColor: alpha(theme.palette.primary.main, 0.4),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.8rem",
      color: theme.palette.text.secondary,
      "&.Mui-focused": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiOutlinedInput-input": {
      py: 1,
      px: 1.5,
    },
  };

  // Shared styles applied to the Autocomplete wrapper so the TextField inside matches
  const bookieFilterSx = {
    "& .MuiTextField-root": {
      "& .MuiOutlinedInput-root": {
        borderRadius: 1.5,
        fontSize: "0.82rem",
        background: isDark
          ? alpha("#ffffff", 0.03)
          : alpha("#ffffff", 0.7),
        "& fieldset": {
          borderColor: isDark
            ? alpha("#ffffff", 0.08)
            : alpha("#000000", 0.1),
        },
        "&:hover fieldset": {
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
        },
      },
      "& .MuiInputLabel-root": {
        fontSize: "0.8rem",
        color: theme.palette.text.secondary,
        "&.Mui-focused": {
          color: theme.palette.primary.main,
        },
      },
    },
  };

  const numericFieldWidth = isMobile ? "100%" : 120;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: 1.5,
        width: "100%",
        flexWrap: "wrap",
        p: 1.5,
        borderRadius: 2,
        background: isDark
          ? alpha("#ffffff", 0.02)
          : alpha("#000000", 0.015),
        border: `1px solid ${isDark ? alpha("#ffffff", 0.06) : alpha("#000000", 0.06)}`,
      }}
    >
      {/* Filter icon label */}
      <FilterListIcon
        sx={{
          fontSize: "1rem",
          color: theme.palette.text.disabled,
          display: { xs: "none", sm: "block" },
        }}
      />

      {/* Bookmakers filter */}
      <Box sx={{ width: isMobile ? "100%" : 300, flexShrink: 0, ...bookieFilterSx }}>
        <ValueBookiesFilter
          bookmakers={bookmakers}
          selectedBookmakers={extraFilters.selectedBookmakers}
          onBookmakersChange={(newBookmakers) =>
            onExtraFiltersChange({ selectedBookmakers: newBookmakers })
          }
          size="small"
        />
      </Box>

      {/* Sports multi-select */}
      <FormControl
        size="small"
        sx={{
          width: isMobile ? "100%" : 180,
          flexShrink: 0,
          ...inputSx,
        }}
      >
        <InputLabel id="sport-select-label">Sports</InputLabel>
        <Select
          labelId="sport-select-label"
          id="sport-select"
          multiple
          value={ensureArray(extraFilters.selectedSport)}
          onChange={(e) =>
            onExtraFiltersChange({ selectedSport: e.target.value })
          }
          input={<OutlinedInput label="Sports" />}
          startAdornment={
            extraFilters.selectedSport?.length > 0 && (
              <InputAdornment position="start" sx={{ ml: 0.5 }}>
                {getSportsIcon(extraFilters.selectedSport[0])}
              </InputAdornment>
            )
          }
          endAdornment={
            extraFilters.selectedSport?.length > 0 && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear sports"
                  onClick={() => onExtraFiltersChange({ selectedSport: [] })}
                  edge="end"
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <ClearIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </InputAdornment>
            )
          }
          renderValue={(selected) => (
            <Typography
              sx={{
                fontSize: "0.8rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selected.join(", ")}
            </Typography>
          )}
        >
          {Object.keys(sportsIcons).map((sport) => (
            <MenuItem key={sport} value={sport} sx={{ fontSize: "0.85rem" }}>
              {sport}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Numeric filters group */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          width: isMobile ? "100%" : "auto",
        }}
      >
        <FormControl
          size="small"
          variant="outlined"
          sx={{ width: numericFieldWidth, ...inputSx }}
        >
          <InputLabel htmlFor="max-odds">Max Odds</InputLabel>
          <OutlinedInput
            id="max-odds"
            type="number"
            label="Max Odds"
            value={extraFilters.maxOdds}
            onChange={(e) => onExtraFiltersChange({ maxOdds: e.target.value })}
          />
        </FormControl>

        <FormControl
          size="small"
          variant="outlined"
          sx={{ width: numericFieldWidth, ...inputSx }}
        >
          <InputLabel htmlFor="bookie-count">Min Bookies</InputLabel>
          <OutlinedInput
            id="bookie-count"
            type="number"
            label="Min Bookies"
            value={extraFilters.bookieCount}
            onChange={(e) =>
              onExtraFiltersChange({ bookieCount: e.target.value })
            }
          />
        </FormControl>

        <FormControl
          size="small"
          variant="outlined"
          sx={{ width: numericFieldWidth, ...inputSx }}
        >
          <InputLabel htmlFor="hours-to-event">Hours</InputLabel>
          <OutlinedInput
            id="hours-to-event"
            type="number"
            label="Hours"
            value={extraFilters.hoursToEvent}
            onChange={(e) =>
              onExtraFiltersChange({ hoursToEvent: e.target.value })
            }
          />
        </FormControl>
      </Box>
    </Box>
  );
};

export default ValuePageFilters;
